import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useBreakpointValue,
  useColorModeValue,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { Edit, Eye } from "lucide-react";
import axios from "../../axiosConfig";
import { useNavigate } from "react-router-dom";
import ListPageLayout from "../common/ListPageLayout";
import TruncatedText from "../common/TruncatedText";

const TITLE_MAX_CHARS = 100;

const ArticlesList = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const toast = useToast();
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const subTextColor = useColorModeValue("gray.600", "gray.400");

  useEffect(() => {
    fetchArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchArticles = async () => {
    try {
      const res = await axios.get("/admin/articles");
      setArticles(res.data ?? []);
    } catch (error) {
      toast({
        title: "Error loading articles",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePublishToggle = async (article) => {
    const action = article.published_at ? "unpublish" : "publish";
    try {
      await axios.patch(`/admin/articles/${article.id}/${action}`);
      toast({
        title: `Article ${action}ed`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      fetchArticles();
    } catch (error) {
      toast({
        title: `Failed to ${action} article`,
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const formatVisibility = (visibility) => {
    if (!visibility) return "—";
    return visibility === "public_article"
      ? "Public"
      : visibility.replaceAll("_", " ");
  };

  const capitalize = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const publishedLabel = (article) => {
    if (!article.published_at) return { text: "No", colorScheme: "gray" };

    return { text: "Yes", colorScheme: "green" };
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString();
    } catch {
      return "";
    }
  };

  const actions = (
    <Button colorScheme="blue" onClick={() => navigate("new")} size="sm">
      Create Article
    </Button>
  );

  const renderMobileCard = (article) => {
    const pub = publishedLabel(article);

    return (
      <Box
        key={article.id}
        p={4}
        border="1px solid"
        borderColor={borderColor}
        borderRadius="xl"
        shadow="sm"
        bg={cardBg}
        _hover={{ shadow: "md" }}
      >
        <VStack align="start" spacing={3} w="100%">
          <Box minW={0} w="100%">
            <TruncatedText
              value={article.title}
              maxChars={TITLE_MAX_CHARS}
              fontWeight="bold"
              fontSize="md"
            />
            <Text fontSize="sm" color={subTextColor}>
              {article.category ? capitalize(article.category) : "—"}
            </Text>
          </Box>

          <HStack spacing={2} flexWrap="wrap">
            <Badge variant="subtle" colorScheme="purple" borderRadius="md">
              {capitalize(formatVisibility(article.visibility))}
            </Badge>

            <Badge variant="subtle" colorScheme={pub.colorScheme} borderRadius="md">
              Published: {pub.text}
              {article.published_at ? ` (${formatDate(article.published_at)})` : ""}
            </Badge>
          </HStack>

          <HStack w="100%" justify="flex-end" spacing={2}>
            <Tooltip label="Edit" hasArrow>
              <IconButton
                size="sm"
                icon={<Edit size={16} />}
                onClick={() => navigate(`/app/articles/${article.id}/edit`)}
                aria-label="Edit"
                title="Edit"
                variant="outline"
                colorScheme="yellow"
              />
            </Tooltip>

            <Tooltip label="Preview" hasArrow>
              <IconButton
                size="sm"
                icon={<Eye size={16} />}
                onClick={() => navigate(`/app/articles/${article.id}/preview`)}
                aria-label="Preview"
                title="Preview"
                variant="outline"
                colorScheme="cyan"
              />
            </Tooltip>

            {/* Optional: if you want publish/unpublish here later:
            <Button size="sm" variant="outline" onClick={() => handlePublishToggle(article)}>
              {article.published_at ? "Unpublish" : "Publish"}
            </Button>
            */}
          </HStack>
        </VStack>
      </Box>
    );
  };

  const renderDesktopTable = () => (
    <Box
      w="100%"
      maxW="100%"
      minW={0}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      bg={cardBg}
      shadow="sm"
      overflow="hidden"
    >
      <TableContainer w="100%" maxW="100%" minW={0} overflowX="auto">
        <Table
          variant="unstyled"
          size="md"
          tableLayout="fixed"
          w="100%"
          maxW="100%"
          sx={{
            "th, td": { borderBottom: "1px solid", borderColor: "gray.100" },
            "tbody tr:hover": { background: "gray.50" },
          }}
        >
          <colgroup>
            <col style={{ width: "44%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "10%" }} />
          </colgroup>

          <Thead>
            <Tr
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                background: cardBg,
              }}
            >
              <Th py={4} fontSize="xs" letterSpacing="wider" textTransform="uppercase">
                Title
              </Th>
              <Th py={4} fontSize="xs" letterSpacing="wider" textTransform="uppercase">
                Category
              </Th>
              <Th py={4} fontSize="xs" letterSpacing="wider" textTransform="uppercase">
                Visibility
              </Th>
              <Th py={4} fontSize="xs" letterSpacing="wider" textTransform="uppercase">
                Published
              </Th>
              <Th
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
                textAlign="right"
              >
                Actions
              </Th>
            </Tr>
          </Thead>

          <Tbody>
            {articles.map((article) => {
              const pub = publishedLabel(article);

              return (
                <Tr key={article.id}>
                  <Td py={3}>
                    <Box minW={0} w="100%">
                      <TruncatedText
                        value={article.title}
                        maxChars={TITLE_MAX_CHARS}
                        fontWeight="semibold"
                      />
                    </Box>
                  </Td>

                  <Td py={3}>
                    <Box minW={0} w="100%">
                      <TruncatedText
                        value={article.category ? capitalize(article.category) : "—"}
                        tooltip={false}
                      />
                    </Box>
                  </Td>

                  <Td py={3}>
                    <Badge variant="subtle" colorScheme="purple" borderRadius="md">
                      {capitalize(formatVisibility(article.visibility))}
                    </Badge>
                  </Td>

                  <Td py={3}>
                    <Badge variant="subtle" colorScheme={pub.colorScheme} borderRadius="md">
                      {pub.text}
                    </Badge>
                    {article.published_at && (
                      <Text as="span" fontSize="xs" fontStyle="italic" color="gray.400" ml={2}>
                        ({formatDate(article.published_at)})
                      </Text>
                    )}
                  </Td>

                  <Td py={3} textAlign="right">
                    <HStack justify="flex-end" spacing={2}>
                      <Tooltip label="Edit" hasArrow>
                        <IconButton
                          size="sm"
                          icon={<Edit size={16} />}
                          onClick={() => navigate(`/app/articles/${article.id}/edit`)}
                          aria-label="Edit"
                          title="Edit"
                          variant="outline"
                          colorScheme="yellow"
                        />
                      </Tooltip>

                      <Tooltip label="Preview" hasArrow>
                        <IconButton
                          size="sm"
                          icon={<Eye size={16} />}
                          onClick={() => navigate(`/app/articles/${article.id}/preview`)}
                          aria-label="Preview"
                          title="Preview"
                          variant="outline"
                          colorScheme="cyan"
                        />
                      </Tooltip>
                    </HStack>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <ListPageLayout title="Articles" actions={actions} contentProps={{ maxW: "100%", minW: 0 }}>
      {loading ? (
        <Flex justify="center" mt={10}>
          <Spinner size="xl" />
        </Flex>
      ) : articles.length === 0 ? (
        <Text mt={6}>No articles found.</Text>
      ) : isMobile ? (
        <VStack spacing={4} mt={2} align="stretch">
          {articles.map(renderMobileCard)}
        </VStack>
      ) : (
        <Box mt={2}>{renderDesktopTable()}</Box>
      )}
    </ListPageLayout>
  );
};

export default ArticlesList;